export type ScenesMap = Record<string, unknown>;

export const serializeScenes = (scenes: ScenesMap | null | undefined): string => JSON.stringify(scenes ?? {});

/**
 * Decides whether the legacy storyEngine.scenes map should be included in a
 * Story Bible save. The map can be megabytes on legacy projects and rarely
 * changes, so sending it on every autosave is wasted upload and a full
 * Firestore field rewrite — and on oversized legacy docs it's what pushes the
 * save past Firestore's 1MiB document limit (SW-9950).
 *
 * - Unchanged scenes are omitted; the server leaves the field untouched.
 * - Empty scenes are always omitted: scenes length should never go down, so
 *   this preserves the old guard against race conditions wiping the map,
 *   while still letting the other Story Bible fields save.
 *
 * Returns the scenes to send (or undefined to omit) plus the serialized form
 * to remember as "last saved" after a successful save.
 */
export const buildScenesForSave = (
  scenes: ScenesMap | null | undefined,
  lastSavedScenesJson: string | null,
): { scenesToSave: ScenesMap | undefined; scenesJson: string | null } => {
  if (!scenes || Object.keys(scenes).length === 0) {
    return { scenesToSave: undefined, scenesJson: null };
  }

  const scenesJson = serializeScenes(scenes);
  if (scenesJson === lastSavedScenesJson) {
    return { scenesToSave: undefined, scenesJson: null };
  }

  return { scenesToSave: scenes, scenesJson };
};
